import { isToday } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

export type TimezoneOption = {
  value: string;
  label: string;
};

export type TimeOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

const TIMEZONE_LIST = [
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Jamaica",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Istanbul",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
] as const;

const formatTimezoneLabel = (timezone: string): string => {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "longOffset",
      hour12: false,
      hour: "numeric",
    });

    const formatted = formatter.format(date);
    const city = timezone.split("/").pop()?.replace(/_/g, " ") ?? timezone;
    const offset = formatted.split(" ").pop() ?? "";

    return `${city} (${offset})`;
  } catch (e) {
    return timezone;
  }
};

export const getTimezones = (): TimezoneOption[] => {
  try {
    return TIMEZONE_LIST.map((zone) => ({
      value: zone,
      label: formatTimezoneLabel(zone),
    })).sort((a, b) => {
      const offsetA = a.label.match(/\(GMT([+-]\d+:\d+)\)/)?.[1];
      const offsetB = b.label.match(/\(GMT([+-]\d+:\d+)\)/)?.[1];

      if (offsetA && offsetB) {
        const numA = parseInt(offsetA.replace(":", ""));
        const numB = parseInt(offsetB.replace(":", ""));
        return numA - numB;
      }

      return a.label.localeCompare(b.label);
    });
  } catch (e) {
    console.error("Error formatting timezone labels:", e);
    return TIMEZONE_LIST.map((zone) => ({
      value: zone,
      label: zone.split("/").pop()?.replace(/_/g, " ") ?? zone,
    }));
  }
};

export const getCurrentHourInTimezone = (timezone: string): number => {
  try {
    const now = new Date();
    const zonedTime = utcToZonedTime(now, timezone);
    return zonedTime.getHours();
  } catch (e) {
    console.error("Error getting current hour in timezone:", e);
    return new Date().getHours();
  }
};

export const getCurrentTimeInTimezone = (
  timezone: string,
): { hour: number; minute: number } => {
  try {
    const now = new Date();
    const zonedTime = utcToZonedTime(now, timezone);
    return {
      hour: zonedTime.getHours(),
      minute: zonedTime.getMinutes(),
    };
  } catch (e) {
    console.error("Error getting current time in timezone:", e);
    const now = new Date();
    return {
      hour: now.getHours(),
      minute: now.getMinutes(),
    };
  }
};

const generateAllTimes = (minTime: string | null = null): TimeOption[] => {
  const times: TimeOption[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hourStr = hour.toString().padStart(2, "0");
      const minuteStr = minute.toString().padStart(2, "0");
      const timeStr = `${hourStr}:${minuteStr}`;

      if (minTime && timeStr < minTime) {
        times.push({
          value: timeStr,
          label: timeStr,
          disabled: true,
        });
      } else {
        times.push({
          value: timeStr,
          label: timeStr,
        });
      }
    }
  }

  return times;
};

// Get the next valid time slot in 15-minute increments
const getNextTimeSlot = (
  hour: number,
  minute: number,
): { hour: number; minute: number } => {
  const nextMinuteIncrement = Math.ceil(minute / 15) * 15;
  let nextMinute;

  if (nextMinuteIncrement <= minute) {
    nextMinute = nextMinuteIncrement + 15;
  } else {
    nextMinute = nextMinuteIncrement;
  }

  const nextHour = hour + Math.floor(nextMinute / 60);
  const normalizedNextMinute = nextMinute % 60;

  return { hour: nextHour, minute: normalizedNextMinute };
};

const generateTimesForToday = (
  timezone: string | undefined,
  minTime: string | null,
): TimeOption[] => {
  const times: TimeOption[] = [];
  const now = new Date();

  try {
    let currentTimeInTimezone;
    if (timezone) {
      const zonedTime = utcToZonedTime(now, timezone);
      currentTimeInTimezone = {
        hour: zonedTime.getHours(),
        minute: zonedTime.getMinutes(),
      };
    } else {
      currentTimeInTimezone = {
        hour: now.getHours(),
        minute: now.getMinutes(),
      };
    }

    // Calculate next valid time slot
    const { hour: nextHour, minute: normalizedNextMinute } = getNextTimeSlot(
      currentTimeInTimezone.hour,
      currentTimeInTimezone.minute,
    );

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = hour.toString().padStart(2, "0");
        const minuteStr = minute.toString().padStart(2, "0");
        const timeStr = `${hourStr}:${minuteStr}`;

        const isBeforeNextTimeSlot =
          hour < nextHour ||
          (hour === nextHour && minute < normalizedNextMinute);

        const isBeforeMinTime = minTime !== null && timeStr < minTime;

        times.push({
          value: timeStr,
          label: timeStr,
          disabled: isBeforeNextTimeSlot || isBeforeMinTime,
        });
      }
    }

    return times;
  } catch (e) {
    console.error("Error generating times for timezone:", e);
    return generateAllTimes(minTime);
  }
};

export const generateTimes = (
  selectedDate: Date | null,
  timezone?: string,
  minTime?: string | null,
): TimeOption[] => {
  if (!selectedDate) return [];

  // Check if selected date is in the past (before today)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to beginning of day for comparison
  const selectedDateCopy = new Date(selectedDate);
  selectedDateCopy.setHours(0, 0, 0, 0);

  // If selected date is in the past, return all times as disabled
  if (selectedDateCopy < today) {
    const times: TimeOption[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = hour.toString().padStart(2, "0");
        const minuteStr = minute.toString().padStart(2, "0");
        const timeStr = `${hourStr}:${minuteStr}`;

        times.push({
          value: timeStr,
          label: timeStr,
          disabled: true,
        });
      }
    }
    return times;
  }

  if (!isToday(selectedDate)) {
    return generateAllTimes(minTime ?? null);
  }

  return generateTimesForToday(timezone, minTime ?? null);
};
